import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
  CashFund,
  CashFundsService,
  CostCenterSummary,
  Identity,
  TransferStatusEnum,
} from '@libs/core/api';
import {
  ImageSliderModalComponent,
  LoadingService,
  ToastService,
} from '@libs/ui/uikit';
import { FormBuilder } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, from, tap } from 'rxjs';
import { AppValidators } from '@front-end/core/utils';
import {
  Attachment,
  getAttachmentAt,
  normalizeOtherAttachments,
  shouldShowAttachmentSlot,
  toOtherAttachmentSlotPatch,
} from '../../fund-cash-attachments.utils';

enum PurchaseDocumentType {
  RECEIPT = 'receipt',
  INVOICE = 'invoice',
}

@UntilDestroy()
@Component({
  selector: 'front-end-fund-cash-purchase-detail-modal',
  templateUrl: './fund-cash-purchase-detail-modal.component.html',
  styleUrls: ['./fund-cash-purchase-detail-modal.component.scss'],
})
export class FundCashPurchaseDetailModalComponent implements OnInit {
  @Input() id?: number;
  @Input() project!: CostCenterSummary;
  @Input() identity!: Identity;

  readonly TransferStatusEnum = TransferStatusEnum;
  readonly PurchaseDocumentType = PurchaseDocumentType;

  constructor(
    private modalController: ModalController,
    private cashFundService: CashFundsService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private fb: FormBuilder
  ) {}

  cashFund$ = new BehaviorSubject<CashFund | null>(null);

  form = this.fb.group(
    {
      receipt: this.fb.control<Attachment | null>(null),
      invoice: this.fb.control<Attachment | null>(null),
      otherAttachment1: this.fb.control<Attachment | null>(null),
      otherAttachment2: this.fb.control<Attachment | null>(null),
      otherAttachment3: this.fb.control<Attachment | null>(null),
      otherAttachment4: this.fb.control<Attachment | null>(null),
      notes: this.fb.control<string | null>(null),
    },
    {
      validators: [AppValidators.atLeastOne('receipt', 'invoice')],
    }
  );

  ngOnInit(): void {
    if (!this.id) return;

    this.cashFundService
      .getCashFund({ cashFundId: this.id })
      .pipe(
        tap((cashFund) => {
          this.cashFund$.next(cashFund);

          const receipt =
            cashFund.receiptFileUrl && cashFund.receiptFileName
              ? {
                  name: cashFund.receiptFileName,
                  url: cashFund.receiptFileUrl,
                }
              : null;

          const invoice =
            cashFund.invoiceFileUrl && cashFund.invoiceFileName
              ? {
                  name: cashFund.invoiceFileName,
                  url: cashFund.invoiceFileUrl,
                }
              : null;

          this.form.patchValue({
            notes: cashFund.comment,
            receipt: receipt,
            invoice: invoice,
          });
          this.patchOtherAttachments(cashFund.otherFiles);
        }),
        this.toastService.catchErrorWithToast(),
        this.loadingService.withLoading(),
        untilDestroyed(this)
      )
      .subscribe();
  }

  close(): void {
    this.modalController.dismiss();
  }

  openAttachment(fileUrl: string): void {
    from(
      this.modalController.create({
        component: ImageSliderModalComponent,
        mode: 'md',
        componentProps: {
          attachments: [fileUrl],
          selectedAttachment: fileUrl,
        },
      })
    ).subscribe((modal) => modal.present());
  }

  get purchaseDocumentType(): PurchaseDocumentType {
    return this.form.value.invoice
      ? PurchaseDocumentType.INVOICE
      : PurchaseDocumentType.RECEIPT;
  }

  get purchaseDocumentAttachment(): Attachment | null {
    return this.form.value.invoice ?? this.form.value.receipt ?? null;
  }

  getOtherAttachment(index: number): Attachment | null {
    return getAttachmentAt(this.getOtherAttachmentSlots(), index);
  }

  shouldShowOtherAttachment(index: number): boolean {
    return shouldShowAttachmentSlot(this.getOtherAttachmentSlots(), index);
  }

  private patchOtherAttachments(
    otherFiles:
      | Attachment[]
      | Array<{ fileName?: null | string; fileUrl?: null | string }>
      | undefined
  ): void {
    this.form.patchValue(
      toOtherAttachmentSlotPatch(normalizeOtherAttachments(otherFiles))
    );
  }

  private getOtherAttachmentSlots(): Array<Attachment | null | undefined> {
    return [
      this.form.value.otherAttachment1,
      this.form.value.otherAttachment2,
      this.form.value.otherAttachment3,
      this.form.value.otherAttachment4,
    ];
  }
}
